import React, { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { Receipt, Description } from "@mui/icons-material";

const InvoiceEstimateTabs = ({ tabValue, handleTabChange, data }) => {
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    if (!chartRef.current) {
      console.warn("Chart ref is null, skipping chart rendering");
      return;
    }

    const summaryData = tabValue === 0 ? data.invoiceSummary : data.estimateSummary;
    if (!summaryData?.length) {
      console.log("No summary data, destroying chart if exists");
      if (chartInstance) {
        chartInstance.destroy();
        setChartInstance(null);
      }
      return;
    }

    const validSummary = summaryData.filter(
      (item) => item !== null && item !== undefined && item._id
    );
    if (!validSummary.length) {
      console.log("No valid summary data, destroying chart if exists");
      if (chartInstance) {
        chartInstance.destroy();
        setChartInstance(null);
      }
      return;
    }

    const allTypes = [
      ...new Set(validSummary.flatMap((item) => (item.types || []).map((t) => t.type).filter(Boolean))),
    ];
    console.log("allTypes:", allTypes);

    const ctx = chartRef.current.getContext("2d");
    if (!ctx) {
      console.warn("Canvas context not available");
      return;
    }

    const datasets = [
      {
        label: "Total Amount ($)",
        data: validSummary.map((item) => item.totalAmount || 0),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderColor: "#3b82f6",
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
      ...allTypes.map((type, index) => ({
        label: `${type} Amount ($)`,
        data: validSummary.map((item) => {
          const typeData = (item.types || []).find((t) => t.type === type);
          return typeData ? typeData.totalAmount : 0;
        }),
        backgroundColor: `hsla(${index * 60}, 70%, 50%, 0.8)`,
        borderColor: `hsl(${index * 60}, 70%, 40%)`,
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      })),
    ];

    if (chartRef.current) {
      if (chartInstance) {
        console.log("Updating existing chart instance");
        chartInstance.data.labels = validSummary.map((item) => item._id);
        chartInstance.data.datasets = datasets;
        try {
          chartInstance.update();
        } catch (err) {
          console.error("Chart update failed:", err);
          chartInstance.destroy();
          setChartInstance(null);
        }
      } else {
        console.log("Creating new chart instance");
        try {
          const newChartInstance = new Chart(ctx, {
            type: "bar",
            data: { labels: validSummary.map((item) => item._id), datasets },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: "top",
                  labels: {
                    color: "#ffffff",
                    font: { size: 12 },
                    usePointStyle: true,
                    padding: 15,
                  },
                },
                tooltip: {
                  mode: "index",
                  backgroundColor: "rgba(0,0,0,0.8)",
                  titleColor: "#ffffff",
                  bodyColor: "#ffffff",
                  borderColor: "#3b82f6",
                  borderWidth: 1,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: { color: "#ffffff" },
                  grid: { color: "rgba(255,255,255,0.1)" },
                },
                x: {
                  ticks: {
                    maxRotation: 45,
                    color: "#ffffff",
                  },
                  grid: { color: "rgba(255,255,255,0.1)" },
                },
              },
            },
          });
          setChartInstance(newChartInstance);
        } catch (err) {
          console.error("Chart creation failed:", err);
        }
      }
    }

    return () => {
      if (chartInstance && chartRef.current) {
        console.log("Cleaning up chart instance");
        chartInstance.destroy();
        setChartInstance(null);
      }
    };
  }, [data, tabValue]);

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Invoice & Estimate Tabs"
        sx={{
          "& .MuiTabs-indicator": { backgroundColor: "#3b82f6" },
          "& .MuiTab-root": {
            color: "rgba(255, 255, 255, 0.7)",
            fontWeight: "bold",
            fontSize: "1rem",
          },
          "& .Mui-selected": { color: "#ffffff !important" },
        }}
      >
        <Tab
          label="Invoice Summary"
          icon={<Receipt />}
          iconPosition="start"
          sx={{ minHeight: "64px" }}
        />
        <Tab
          label="Estimate Summary"
          icon={<Description />}
          iconPosition="start"
          sx={{ minHeight: "64px" }}
        />
      </Tabs>

      <Box sx={{ mt: 3 }}>
        {tabValue === 0 && (
          <Card
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ color: "#ffffff", mb: 3, fontWeight: "bold" }}
              >
                Invoice Summary Analysis
              </Typography>
              <Box
                sx={{
                  height: "450px",
                  mb: 4,
                  p: 2,
                  bgcolor: "rgba(0,0,0,0.2)",
                  borderRadius: 2,
                }}
              >
                {data.invoiceSummary?.length ? (
                  <canvas ref={chartRef} id="invoiceChart" />
                ) : (
                  <Typography sx={{ color: "#ffffff", textAlign: "center" }}>
                    No data available for the selected date range
                  </Typography>
                )}
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          bgcolor: "rgba(59, 130, 246, 0.2)",
                          color: "#ffffff",
                          fontWeight: "bold",
                        },
                      }}
                    >
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Total Invoices</TableCell>
                      <TableCell align="center">Total Amount</TableCell>
                      <TableCell align="center">Avg Amount</TableCell>
                      {data.invoiceSummary?.[0]?.types?.[0]?.taxBreakdown &&
                      Object.keys(data.invoiceSummary[0].types[0].taxBreakdown).length > 0 ? (
                        Object.keys(data.invoiceSummary[0].types[0].taxBreakdown).map((tax, i) => (
                          <TableCell key={i} align="center">
                            {tax}
                          </TableCell>
                        ))
                      ) : (
                        <TableCell align="center">Taxes</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.invoiceSummary?.length ? (
                      data.invoiceSummary
                        .filter((item) => item !== null && item !== undefined)
                        .map((item, index) => (
                          <TableRow
                            key={item._id || index}
                            sx={{
                              "& td": {
                                color: "#ffffff",
                                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                              },
                              "&:hover": { bgcolor: "rgba(255, 255, 255, 0.05)" },
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={item._id || "Unknown"}
                                sx={{
                                  bgcolor: `hsl(${index * 60}, 70%, 50%)`,
                                  color: "#ffffff",
                                  fontWeight: "bold",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {item.totalCount?.toLocaleString() || 0}
                            </TableCell>
                            <TableCell align="center">
                              ${item.totalAmount?.toLocaleString() || 0}
                            </TableCell>
                            <TableCell align="center">
                              ${item.avgAmount?.toLocaleString() || 0}
                            </TableCell>
                            {item.types?.[0]?.taxBreakdown &&
                            Object.keys(item.types[0].taxBreakdown).length > 0 ? (
                              Object.entries(item.types[0].taxBreakdown).map(([taxType, amount], i) => (
                                <TableCell key={i} align="center">
                                  ${amount?.toLocaleString() || 0}
                                </TableCell>
                              ))
                            ) : (
                              <TableCell align="center">-</TableCell>
                            )}
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: "#ffffff" }}>
                          No invoices available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {tabValue === 1 && (
          <Card
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ color: "#ffffff", mb: 3, fontWeight: "bold" }}
              >
                Estimate Summary Analysis
              </Typography>
              <Box
                sx={{
                  height: "450px",
                  mb: 4,
                  p: 2,
                  bgcolor: "rgba(0,0,0,0.2)",
                  borderRadius: 2,
                }}
              >
                {data.estimateSummary?.length ? (
                  <canvas ref={chartRef} id="estimateChart" />
                ) : (
                  <Typography sx={{ color: "#ffffff", textAlign: "center" }}>
                    No data available for the selected date range
                  </Typography>
                )}
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          bgcolor: "rgba(139, 92, 246, 0.2)",
                          color: "#ffffff",
                          fontWeight: "bold",
                        },
                      }}
                    >
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Total Estimates</TableCell>
                      <TableCell align="center">Total Amount</TableCell>
                      <TableCell align="center">Avg Amount</TableCell>
                      {data.estimateSummary?.[0]?.types?.[0]?.taxBreakdown &&
                      Object.keys(data.estimateSummary[0].types[0].taxBreakdown).length > 0 ? (
                        Object.keys(data.estimateSummary[0].types[0].taxBreakdown).map((tax, i) => (
                          <TableCell key={i} align="center">
                            {tax}
                          </TableCell>
                        ))
                      ) : (
                        <TableCell align="center">Taxes</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.estimateSummary?.length ? (
                      data.estimateSummary
                        .filter((item) => item !== null && item !== undefined)
                        .map((item, index) => (
                          <TableRow
                            key={item._id || index}
                            sx={{
                              "& td": {
                                color: "#ffffff",
                                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                              },
                              "&:hover": { bgcolor: "rgba(255, 255, 255, 0.05)" },
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={item._id || "Unknown"}
                                sx={{
                                  bgcolor: `hsl(${index * 60}, 70%, 50%)`,
                                  color: "#ffffff",
                                  fontWeight: "bold",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {item.totalCount?.toLocaleString() || 0}
                            </TableCell>
                            <TableCell align="center">
                              ${item.totalAmount?.toLocaleString() || 0}
                            </TableCell>
                            <TableCell align="center">
                              ${item.avgAmount?.toLocaleString() || 0}
                            </TableCell>
                            {item.types?.[0]?.taxBreakdown &&
                            Object.keys(item.types[0].taxBreakdown).length > 0 ? (
                              Object.entries(item.types[0].taxBreakdown).map(([taxType, amount], i) => (
                                <TableCell key={i} align="center">
                                  ${amount?.toLocaleString() || 0}
                                </TableCell>
                              ))
                            ) : (
                              <TableCell align="center">-</TableCell>
                            )}
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: "#ffffff" }}>
                          No estimates available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default InvoiceEstimateTabs;